/**
 * useProfileSEOMetadata Hook
 *
 * SEO metadata for profile pages via the resolver RPC (shared react-query
 * cache with useLocalizedProfileMetadata — one RPC per (profile, locale)).
 * Also generates hreflang links (all pointing to same canonical URL per
 * identity invariant).
 *
 * MIGRATION (2026-06-07, profile localization pass Brief 4): SEO fields now
 * come from resolve_profiles_for_listing (server-side cascade), and the hook
 * exposes `noIndex` for the robots meta.
 *
 * THE CONTRACT:
 *   no_index → robots meta ONLY (never a render gate);
 *   meta_title is null by design for profiles — display_name is the title.
 *
 * Profile hreflang invariant: URL never changes across locales.
 * All hreflang entries point to the same /{vanity_url} canonical URL.
 */

import { useMemo } from 'react';
import { usePrimaryLocaleConfigs } from './useLocaleConfigs';
import { generateProfileHreflangLinks } from '@/lib/hreflangUtils';
import { useResolvedProfileListing } from './useLocalizedProfileMetadata';

interface ProfileSEOMeta {
  meta_title: string | null;
  meta_description: string | null;
  aio_snippet: string | null;
  schema_about: string | null;
}

interface ProfileSEOMetadataResult {
  seoMeta: ProfileSEOMeta | null;
  hreflangLinks: Array<{ lang: string; url: string }>;
  isLoading: boolean;
  /** Robots meta ONLY — never use as a render gate */
  noIndex: boolean;
  /** Always "/{vanity_url}" — enforce on the page, never UUID */
  canonicalPath: string | null;
}

/**
 * Hook to fetch profile SEO metadata and generate hreflang links.
 *
 * SEO fields come from the resolver RPC for the current locale.
 * hreflang links all point to the SAME canonical URL (identity invariant).
 */
export function useProfileSEOMetadata(
  profileId: string | undefined,
  vanityUrl: string | null | undefined
): ProfileSEOMetadataResult {
  const { data, isLoading } = useResolvedProfileListing(profileId);

  const seoMeta: ProfileSEOMeta | null = data
    ? {
        meta_title: data.localized.meta_title, // null by design — display_name is the title
        meta_description: data.localized.meta_description,
        aio_snippet: data.localized.aio_snippet,
        schema_about: data.localized.schema_about,
      }
    : null;

  // Derive hreflang links from cached primary locale configs (system_locale_configs)
  const { data: primaryLocales } = usePrimaryLocaleConfigs();

  const hreflangLinks = useMemo(() => {
    if (!vanityUrl || !primaryLocales?.length) return [];
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const canonicalUrl = `${baseUrl}/${vanityUrl}`;
    return generateProfileHreflangLinks(canonicalUrl, primaryLocales);
  }, [vanityUrl, primaryLocales]);

  // Dev-only: assert 36 primary locales + x-default = 37 entries
  if (import.meta.env.DEV && hreflangLinks.length > 0 && primaryLocales?.length) {
    const expected = primaryLocales.length + 1;
    if (hreflangLinks.length !== expected) {
      // eslint-disable-next-line no-console
      console.warn('[SEO] Profile hreflang invariant violated', {
        expected,
        actual: hreflangLinks.length,
      });
    }
  }

  return {
    seoMeta,
    hreflangLinks,
    isLoading,
    noIndex: data?.localized.no_index ?? false,
    canonicalPath: data?.canonical_path ?? null,
  };
}
